import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Brain, Loader2, RefreshCw, TrendingUp, TrendingDown, DollarSign, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AIAdvisor({ branchId, token, t, lang, onDataReady }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cached, setCached] = useState(null);
  const fmt = (v) => Number(v || 0).toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Load cached on mount
  useEffect(() => {
    if (!branchId) return;
    axios.get(`${API}/api/accounting/ai-advisor/cached?branch_id=${branchId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data?.analysis) setCached(res.data); })
      .catch(() => {});
  }, [branchId, token]);

  const generateAnalysis = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.post(`${API}/api/accounting/ai-advisor?branch_id=${branchId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setData(d);
      setCached(null);
      if (onDataReady) onDataReady(d);
      toast.success(t('advisor.generated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('common.failed'));
    }
    setLoading(false);
  };

  const active = data || cached;
  const summary = active?.financial_summary;

  return (
    <div className="space-y-5" data-testid="ai-advisor-tab">
      {/* Header */}
      <Card className="border-0 bg-gradient-to-br from-amber-500/5 to-orange-500/10">
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">{t('advisor.title')}</h2>
                <p className="text-xs text-muted-foreground">{t('advisor.subtitle')}</p>
              </div>
            </div>
            <Button onClick={generateAnalysis} disabled={loading}
              className="bg-gradient-to-r from-amber-500 to-orange-600 text-white gap-2" data-testid="generate-advice-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? t('advisor.analyzing') : t('advisor.generate')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Financial Summary */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('accounting.totalRevenue')}</p>
            <p className="text-lg font-bold text-emerald-500">{fmt(summary.revenue)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('accounting.totalExpenses')}</p>
            <p className="text-lg font-bold text-red-500">{fmt(summary.expenses)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('accounting.netProfit')}</p>
            <p className={`text-lg font-bold ${summary.profit >= 0 ? 'text-amber-500' : 'text-red-500'}`}>{fmt(summary.profit)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">{t('accounting.profitMargin')}</p>
            <p className={`text-lg font-bold ${summary.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{summary.margin}%</p>
          </CardContent></Card>
        </div>
      )}

      {/* AI Analysis Content */}
      {active?.analysis ? (
        <Card data-testid="ai-analysis-card">
          <CardContent className="p-6">
            {cached && !data && (
              <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
                <Badge variant="outline" className="text-[10px]">{t('advisor.cached')}</Badge>
                <span>{new Date(cached.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US')}</span>
              </div>
            )}
            <div className="prose prose-sm dark:prose-invert max-w-none text-foreground leading-relaxed whitespace-pre-wrap"
              dir="rtl" data-testid="ai-analysis-content">
              {active.analysis}
            </div>
          </CardContent>
        </Card>
      ) : !loading ? (
        <div className="text-center py-16" data-testid="advisor-empty">
          <Brain className="w-16 h-16 mx-auto text-muted-foreground/20 mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">{t('advisor.noAnalysis')}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">{t('advisor.noAnalysisDesc')}</p>
        </div>
      ) : (
        <div className="text-center py-16">
          <Loader2 className="w-10 h-10 mx-auto text-amber-500 animate-spin mb-4" />
          <p className="text-sm text-muted-foreground">{t('advisor.analyzing')}</p>
        </div>
      )}
    </div>
  );
}
